"use client"

import type React from "react"
import { useEffect, useState, useMemo, useCallback } from "react"
import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { collection, getDocs, query, where } from "firebase/firestore"
import { db } from "../firebase/config"
import LoadingSpinner from "../components/common/LoadingSpinner"
import LazyImage from "../components/common/LazyImage"
import SEOHead from "../components/common/SEOHead"
import ProductCard from "../components/common/ProductCard" // Add this import
import { X, Star, IndianRupee, Grid, List, Filter, ArrowLeft, Box } from "lucide-react"
import toast from "react-hot-toast"

interface Category {
  id: string
  name: string
  slug: string
  description: string
  image: string
  productCount: number
  isActive: boolean
  tags: string[]
}

interface Product {
  id: string
  name: string
  slug: string
  categories: string[] // Array of category slugs
  categoryNames?: string[] // Array of category names for display
  price: number
  finalPrice: number
  originalPrice?: number
  discount?: number
  images: string[]
  thumbnail: string
  description: string
  isActive: boolean
  stock: number
  tags: string[]
  rating: number
  numRatings: number
  reviews?: number // Add this for ProductCard compatibility
  isFeatured: boolean
  isNewArrival: boolean
  isBestSeller: boolean
  createdAt?: string
  category?: string // Add this for ProductCard compatibility
}

const Categories: React.FC = () => {
  const { category } = useParams<{ category: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [isLoading, setIsLoading] = useState(true)
  const [categories, setCategories] = useState<Category[]>([])
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null)
  const [products, setProductsState] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])

  const [localFilters, setLocalFilters] = useState({
    priceRange: [0, 100000] as [number, number],
    rating: 0,
    sortBy: "name",
    inStock: false,
    search: "",
  })

  // Category display name mapping
  const categoryNames: { [key: string]: string } = {
    sofas: "Sofas",
    "wooden-sofa": "Wooden Sofas",
    "fabric-sofa": "Fabric Sofas",
    "l-shaped-sofa": "L-Shaped Sofas",
    dining: "Dining Sets",
    wardrobes: "Wardrobes",
    chairs: "Chairs",
    tables: "Tables",
    "tv-units": "TV Units",
    "modular-kitchen": "Modular Kitchen",
    "safety-door": "Safety Doors",
    mandir: "Mandirs",
    beds: "Beds",
  }

  useEffect(() => {
    loadData()
  }, [category])

  useEffect(() => {
    // Apply URL search params
    const sort = searchParams.get("sort")
    const minPrice = searchParams.get("minPrice")
    const maxPrice = searchParams.get("maxPrice")
    const rating = searchParams.get("rating")
    const search = searchParams.get("search")

    const newFilters: any = {}
    if (sort) newFilters.sortBy = sort
    if (search) newFilters.search = search
    if (minPrice || maxPrice) {
      newFilters.priceRange = [minPrice ? Number.parseInt(minPrice) : 0, maxPrice ? Number.parseInt(maxPrice) : 100000]
    }
    if (rating) newFilters.rating = Number.parseInt(rating)

    if (Object.keys(newFilters).length > 0) {
      setLocalFilters((prev) => ({ ...prev, ...newFilters }))
    }
  }, [searchParams])

  useEffect(() => {
    filterAndSortProducts()
  }, [products, localFilters])

  const loadData = async () => {
    try {
      setIsLoading(true)

      // Load categories from Firebase
      const categoriesRef = collection(db, "categories")
      const categoriesSnapshot = await getDocs(categoriesRef)
      const categoriesData: Category[] = []

      categoriesSnapshot.forEach((doc) => {
        const data = doc.data()
        categoriesData.push({
          id: doc.id,
          ...data,
          tags: Array.isArray(data.tags) ? data.tags : [],
        } as Category)
      })

      // Load products from Firebase
      const productsRef = collection(db, "products")
      const productsQuery = query(productsRef, where("isActive", "==", true))
      const productsSnapshot = await getDocs(productsQuery)
      const productsData: Product[] = []

      productsSnapshot.forEach((doc) => {
        const data = doc.data()
        productsData.push({
          id: doc.id,
          ...data,
          categories: Array.isArray(data.categories) ? data.categories : [data.category].filter(Boolean),
          categoryNames: data.categoryNames || [],
          tags: Array.isArray(data.tags) ? data.tags : [],
          reviews: data.reviews || data.numRatings || 0, // Add reviews field for ProductCard
          category: data.category || (Array.isArray(data.categories) ? data.categories[0] : ''), // Add category field for ProductCard
        } as Product)
      })

      // Calculate product counts for categories using the new tag-based system
      const categoriesWithCounts = categoriesData.map((cat) => {
        const categoryProducts = productsData.filter((product) => {
          // Check if product belongs to this category
          const hasInCategories = product.categories.includes(cat.slug)
          const hasInTags =
            product.tags.includes(cat.slug) ||
            product.tags.includes(cat.name.toLowerCase()) ||
            product.tags.includes(cat.name.toLowerCase().replace(/\s+/g, "-"))

          return hasInCategories || hasInTags
        })

        return {
          ...cat,
          productCount: categoryProducts.length,
        }
      })

      setCategories(categoriesWithCounts)

      if (category) {
        // Load products for specific category
        const categoryProducts = productsData.filter((product) => {
          // Check if product belongs to this category using the new system
          const hasInCategories = product.categories.includes(category)
          const hasInTags =
            product.tags.includes(category) ||
            product.tags.includes(categoryNames[category]?.toLowerCase()) ||
            product.tags.includes(categoryNames[category]?.toLowerCase().replace(/\s+/g, "-"))

          return hasInCategories || hasInTags
        })

        console.log(`Found ${categoryProducts.length} products for category: ${category}`)

        if (categoryProducts.length === 0) {
          toast.error(`No products found in ${categoryNames[category] || category} category`)
        }

        setProductsState(categoryProducts)

        // Find current category
        const current = categoriesWithCounts.find((c) => c.slug === category)
        setCurrentCategory(current || null)

        // Set price range based on products
        if (categoryProducts.length > 0) {
          const prices = categoryProducts.map((p) => p.finalPrice || p.price)
          setLocalFilters((prev) => ({
            ...prev,
            priceRange: [Math.min(...prices), Math.max(...prices)],
          }))
        }
      } else {
        // Load all products
        setProductsState(productsData)
        setCurrentCategory(null)
      }
    } catch (error) {
      console.error("Error loading data:", error)
      toast.error("Failed to load products")
    } finally {
      setIsLoading(false)
    }
  }

  const filterAndSortProducts = () => {
    let filtered = [...products]

    // Search filter
    if (localFilters.search) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(localFilters.search.toLowerCase()) ||
          product.description.toLowerCase().includes(localFilters.search.toLowerCase()) ||
          product.tags.some((tag) => tag.toLowerCase().includes(localFilters.search.toLowerCase())),
      )
    }

    // Price filter
    filtered = filtered.filter((product) => {
      const price = product.finalPrice || product.price
      return price >= localFilters.priceRange[0] && price <= localFilters.priceRange[1]
    })

    // Rating filter
    if (localFilters.rating > 0) {
      filtered = filtered.filter((product) => product.rating >= localFilters.rating)
    }

    // Stock filter
    if (localFilters.inStock) {
      filtered = filtered.filter((product) => product.stock > 0)
    }

    // Sort products
    switch (localFilters.sortBy) {
      case "name":
        filtered.sort((a, b) => a.name.localeCompare(b.name))
        break
      case "price-low":
        filtered.sort((a, b) => (a.finalPrice || a.price) - (b.finalPrice || b.price))
        break
      case "price-high":
        filtered.sort((a, b) => (b.finalPrice || b.price) - (a.finalPrice || a.price))
        break
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating)
        break
      case "newest":
        filtered.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
        break
      default:
        break
    }

    setFilteredProducts(filtered)
  }

  const handleFilterChange = (filterType: string, value: any) => {
    const newFilters = { ...localFilters, [filterType]: value }
    setLocalFilters(newFilters)

    // Update URL params
    const params = new URLSearchParams(searchParams)
    if (filterType === "sortBy") {
      params.set("sort", value as string)
    } else if (filterType === "priceRange") {
      const [min, max] = value as [number, number]
      params.set("minPrice", min.toString())
      params.set("maxPrice", max.toString())
    } else if (filterType === "rating") {
      if (value) {
        params.set("rating", (value as number).toString())
      } else {
        params.delete("rating")
      }
    } else if (filterType === "search") {
      if (value) {
        params.set("search", value as string)
      } else {
        params.delete("search")
      }
    }
    setSearchParams(params)
  }

  const clearAllFilters = () => {
    setLocalFilters({
      priceRange: [0, 100000],
      rating: 0,
      sortBy: "name",
      inStock: false,
      search: "",
    })
    setSearchParams({})
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (localFilters.rating > 0) count++
    if (localFilters.priceRange[0] > 0 || localFilters.priceRange[1] < 100000) count++
    if (localFilters.inStock) count++
    if (localFilters.search) count++
    return count
  }

  // Remove this function since ProductCard handles navigation internally
  // const handleProductClick = (product: Product) => {
  //   const primaryCategory = product.categories[0] || "products"
  //   const productSlug = product.slug || product.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")
  //   navigate(`/categories/${primaryCategory}/${productSlug}`)
  // }

  // Memoize filtered products for rendering
  const memoizedProducts = useMemo(() => filteredProducts, [filteredProducts])

  // Memoize event handlers
  const memoizedHandleFilterChange = useCallback(handleFilterChange, [localFilters, searchParams])
  const memoizedClearAllFilters = useCallback(clearAllFilters, [])
  const memoizedGetActiveFiltersCount = useCallback(getActiveFiltersCount, [localFilters])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // SEO data for category pages
  const getSEOData = () => {
    if (!category) {
      return {
        title: "Shop by Category - Premium Furniture Collections | Anmol Furniture",
        description:
          "Explore our furniture categories including beds, sofas, dining sets, wardrobes, chairs & tables. Find the perfect furniture for every room in your home.",
        keywords: "furniture categories, beds, sofas, dining furniture, wardrobes, chairs, tables, home furniture",
      }
    }
    const categoryName = categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1)
    return {
      title: `${categoryName} - Premium ${categoryName} Collection | Anmol Furniture`,
      description: `Discover our premium ${categoryName.toLowerCase()} collection. High-quality ${categoryName.toLowerCase()} with free delivery, warranty & easy returns. Shop now!`,
      keywords: `${categoryName.toLowerCase()}, premium ${categoryName.toLowerCase()}, ${categoryName.toLowerCase()} furniture, buy ${categoryName.toLowerCase()} online`,
    }
  }

  const seoData = getSEOData()
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: category
      ? `${categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1)} Category`
      : "All Categories",
    itemListElement: memoizedProducts.map((product, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `https://anmolfurniture.com/products/${product.id}`,
      name: product.name,
      image: product.images?.[0],
      description: product.description,
    })),
  }

  // If no category is specified, show category grid
  if (!category) {
    return (
      <>
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          structuredData={structuredData}
        />
        <main className="min-h-screen bg-gray-50">
          <div className="container mx-auto px-4 py-6 sm:py-8">
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Shop by Category</h1>
              <p className="text-gray-600 text-sm sm:text-base">Discover our wide range of furniture categories</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {(() => {
                const withProducts = categories.filter((cat) => cat.productCount > 0)
                const withoutProducts = categories.filter((cat) => cat.productCount === 0)
                const orderedCategories = [...withProducts, ...withoutProducts]
                return orderedCategories.map((cat) => (
                  <article
                    key={cat.id}
                    onClick={() => navigate(`/categories/${cat.slug}`)}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/categories/${cat.slug}`)
                      }
                    }}
                    aria-label={`Browse ${cat.name} furniture - ${cat.productCount} products available`}
                  >
                    <div className="aspect-square overflow-hidden">
                      <LazyImage
                        src={cat.image || "/placeholder.svg?height=300&width=300"}
                        alt={`${cat.name} furniture collection`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        width={300}
                        height={300}
                      />
                    </div>
                    <div className="p-4 sm:p-6">
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{cat.name}</h2>
                      <p className="text-gray-600 text-sm mb-3">{cat.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-primary-600 font-medium text-sm sm:text-base">
                          {cat.productCount} Products
                        </span>
                        <span className="text-primary-600 group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </div>
                  </article>
                ))
              })()}
            </div>
          </div>
        </main>
      </>
    )
  }

  const categoryDisplayName = categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1)

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={structuredData}
      />
      <main className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6 sm:py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 mb-4 sm:mb-6 text-sm" aria-label="Breadcrumb">
            <button
              onClick={() => navigate("/categories")}
              className="flex items-center space-x-1 text-gray-600 hover:text-primary-600"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Categories</span>
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">{categoryDisplayName}</span>
          </nav>

          {/* Header */}
          <div className="mb-6 sm:mb-8">
            {currentCategory && (
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-4 sm:mb-6">
                <LazyImage
                  src={currentCategory.image || "/placeholder.svg?height=80&width=80"}
                  alt={`${currentCategory.name} category`}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg"
                  width={80}
                  height={80}
                />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{currentCategory.name}</h1>
                  <p className="text-gray-600 text-sm sm:text-base">{currentCategory.description}</p>
                </div>
              </div>
            )}
            <p className="text-gray-600 text-sm sm:text-base">{filteredProducts.length} products found</p>
          </div>

          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <input
              type="text"
              placeholder={`Search in ${categoryDisplayName}...`}
              value={localFilters.search}
              onChange={(e) => memoizedHandleFilterChange("search", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm sm:text-base"
              aria-label={`Search products in ${categoryDisplayName} category`}
            />
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex items-center space-x-4">
              {/* Mobile Filter Button */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="md:hidden flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Filter className="w-5 h-5" />
                <span>Filters</span>
                {memoizedGetActiveFiltersCount() > 0 && (
                  <span className="bg-primary-600 text-white text-xs px-2 py-1 rounded-full">
                    {memoizedGetActiveFiltersCount()}
                  </span>
                )}
              </button>
              {/* Clear Filters */}
              {memoizedGetActiveFiltersCount() > 0 && (
                <button
                  onClick={memoizedClearAllFilters}
                  className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 text-sm"
                >
                  <X className="w-4 h-4" />
                  <span>Clear Filters</span>
                </button>
              )}
            </div>
            <div className="flex items-center space-x-4">
              {/* Sort Dropdown */}
              <select
                value={localFilters.sortBy}
                onChange={(e) => memoizedHandleFilterChange("sortBy", e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                aria-label="Sort products"
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest First</option>
              </select>
              {/* View Mode Toggle */}
              <div
                className="flex border border-gray-300 rounded-lg overflow-hidden"
                role="group"
                aria-label="View mode"
              >
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === "grid" ? "bg-primary-600 text-white" : "bg-white text-gray-600"}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === "grid"}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-primary-600 text-white" : "bg-white text-gray-600"}`}
                  aria-label="List view"
                  aria-pressed={viewMode === "list"}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6 sm:gap-8">
            {/* Filters Sidebar */}
            <aside
              className={`w-full md:w-64 bg-white rounded-lg shadow-md p-4 sm:p-6 ${isFilterOpen ? "block" : "hidden md:block"}`}
              aria-label="Product filters"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button onClick={() => setIsFilterOpen(false)} className="md:hidden" aria-label="Close filters">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Price Range */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Price Range</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <IndianRupee className="w-4 h-4 text-gray-500" />
                    <input
                      type="number"
                      value={localFilters.priceRange[0]}
                      onChange={(e) =>
                        memoizedHandleFilterChange("priceRange", [Number(e.target.value), localFilters.priceRange[1]])
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      placeholder="Min"
                      aria-label="Minimum price"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <IndianRupee className="w-4 h-4 text-gray-500" />
                    <input
                      type="number"
                      value={localFilters.priceRange[1]}
                      onChange={(e) =>
                        memoizedHandleFilterChange("priceRange", [localFilters.priceRange[0], Number(e.target.value)])
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      placeholder="Max"
                      aria-label="Maximum price"
                    />
                  </div>
                </div>
              </div>
              {/* Rating */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Rating</h3>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="rating"
                      checked={localFilters.rating === 0}
                      onChange={() => memoizedHandleFilterChange("rating", 0)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">All Ratings</span>
                  </label>
                  {[4, 3, 2, 1].map((rating) => (
                    <label key={rating} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="rating"
                        checked={localFilters.rating === rating}
                        onChange={() => memoizedHandleFilterChange("rating", rating)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex items-center">
                        {Array.from({ length: rating }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                        <span className="text-sm text-gray-700 ml-2">& Up</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {/* Stock */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Availability</h3>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={localFilters.inStock}
                    onChange={(e) => memoizedHandleFilterChange("inStock", e.target.checked)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">In Stock Only</span>
                </label>
              </div>
            </aside>

            {/* Products Grid/List */}
            <section className="flex-1" aria-label="Products">
              {memoizedProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Box className="w-16 h-16 text-primary-500 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">No products in this category yet</h3>
                  <p className="text-gray-600 mb-6">
                    We're working hard to add amazing products here. Please check back soon!
                  </p>
                  <button
                    onClick={() => navigate("/categories")}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    Browse All Categories
                  </button>
                </div>
              ) : (
                <div
                  className={
                    viewMode === "grid"
                      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                      : "space-y-4 sm:space-y-6"
                  }
                >
                  {/* Use ProductCard component for both Grid and List views */}
                  {memoizedProducts.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product as any}
                      viewMode={viewMode} // Pass the current view mode
                      priority={index < 4} // Prioritize loading for first 4 products
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </>
  )
}

export default Categories
